import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { DEFAULT_SITE_KEYWORDS, DEFAULT_SITE_META_DESCRIPTION } from "../seoConstants";
import "../styles/travelDetail.css";

const SITE_ORIGIN = "https://slatkobezglutena.com";
const DEFAULT_DOC_TITLE = "Slatko bez glutena - Bezglutenski recepti s okusom doma";
const DEFAULT_OG_IMAGE = "https://slatkobezglutena.com/logo512.png";

export default function TravelDetail() {
  const { id } = useParams();
  const [travel, setTravel] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorScreen, setErrorScreen] = useState(false);
  const [shareHint, setShareHint] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        // Try to load with travel_images first
        let { data, error } = await supabase
          .from("travels")
          .select("*, travel_images(*)")
          .eq("id", id)
          .single();

        // If travel_images table doesn't exist, fallback to basic select
        if (error && error.message.includes("Could not find")) {
          const { data: basicData, error: basicError } = await supabase
            .from("travels")
            .select("*")
            .eq("id", id)
            .single();
          
          if (basicError || !basicData) {
            throw basicError || new Error("Travel not found");
          }
          
          data = { ...basicData, travel_images: [] };
          error = null;
        }

        if (error || !data) {
          throw error || new Error("Travel not found");
        }

        setTravel(data);

        // Load recommendations if table exists
        try {
          const { data: recData, error: recError } = await supabase
            .from("travel_recommendations")
            .select("*")
            .eq("travel_id", id)
            .order("order", { ascending: true });

          if (!recError && recData) {
            setRecommendations(recData);
          }
        } catch (recErr) {
          console.log("travel_recommendations table may not exist:", recErr);
        }
      } catch (err) {
        console.error("Greška pri učitavanju putovanja:", err);
        setErrorScreen(true);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [id]);

  useEffect(() => {
    if (!travel) return undefined;

    const pageUrl = `${window.location.origin}/putovanja/${id}`;
    const shortDesc = (travel.description || "").slice(0, 160);
    const heroImage = travel.travel_images?.[0]?.image_url || travel.image_url;
    const ogImage =
      heroImage && /^https?:\/\//i.test(String(heroImage)) ? heroImage : DEFAULT_OG_IMAGE;

    document.title = `${travel.title} | Slatko bez glutena`;

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", pageUrl);

    const setOg = (prop, value) => {
      const el = document.querySelector(`meta[property="${prop}"]`);
      if (el) el.setAttribute("content", value);
    };
    setOg("og:url", pageUrl);
    setOg("og:title", travel.title);
    setOg("og:image", ogImage);
    if (shortDesc) {
      setOg("og:description", shortDesc);
    }

    const twUrl = document.querySelector('meta[name="twitter:url"]');
    if (twUrl) twUrl.setAttribute("content", pageUrl);
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute("content", travel.title);
    const twImg = document.querySelector('meta[name="twitter:image"]');
    if (twImg) twImg.setAttribute("content", ogImage);

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && shortDesc) metaDesc.setAttribute("content", shortDesc);

    const travelKeywordsExtra =
      "putovanja, gluten free putovanje, bezglutenski restorani, savjeti za putovanje, hrana u inozemstvu, celijakija putovanje";
    const keywordsContent = `${DEFAULT_SITE_KEYWORDS}, ${travelKeywordsExtra}`;
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute("content", keywordsContent);

    return () => {
      document.title = DEFAULT_DOC_TITLE;
      if (canonical) canonical.setAttribute("href", SITE_ORIGIN);
      setOg("og:url", SITE_ORIGIN);
      setOg("og:title", DEFAULT_DOC_TITLE);
      setOg("og:description", DEFAULT_SITE_META_DESCRIPTION);
      setOg("og:image", DEFAULT_OG_IMAGE);
      if (twUrl) twUrl.setAttribute("content", SITE_ORIGIN);
      if (twTitle) twTitle.setAttribute("content", DEFAULT_DOC_TITLE);
      if (twImg) twImg.setAttribute("content", DEFAULT_OG_IMAGE);
      if (metaDesc) metaDesc.setAttribute("content", DEFAULT_SITE_META_DESCRIPTION);
      if (metaKw) metaKw.setAttribute("content", DEFAULT_SITE_KEYWORDS);
    };
  }, [travel, id]);

  async function handleShareTravel() {
    const url = window.location.href;
    const title = travel.title;
    const text = travel.description || "";

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        setShareHint(null);
      } catch (err) {
        if (err && err.name === "AbortError") return;
        await copyTravelLink(url);
      }
    } else {
      await copyTravelLink(url);
    }
  }

  async function copyTravelLink(url) {
    try {
      await navigator.clipboard.writeText(url);
      setShareHint("Link je kopiran.");
      setTimeout(() => setShareHint(null), 2500);
    } catch {
      window.prompt("Kopirajte link na putovanje:", url);
    }
  }

  if (loading)
    return <p className="loading">Učitavanje putovanja…</p>;

  if (errorScreen)
    return (
      <div className="error-message">
        <h2>Ups! Nešto je pošlo po zlu</h2>
        <p>Nije moguće učitati putovanje. Molimo pokušajte ponovo.</p>
        <Link to="/putovanja" className="btn-back">← Vrati se na putovanja</Link>
      </div>
    );

  if (!travel) return null;

  const mainImage = travel.travel_images?.[0]?.image_url || travel.image_url;

  return (
    <div className="travel-detail">
      {mainImage && (
        <div className="travel-hero">
          <img src={mainImage} alt={travel.title} />
        </div>
      )}

      <div className="travel-header">
        <h1>{travel.title}</h1>
      </div>

      {travel.description && (
        <div className="travel-body">
          <p className="travel-description">{travel.description}</p>
        </div>
      )}

      {/* Additional images if available */}
      {travel.travel_images && travel.travel_images.length > 1 && (
        <div className="more-images">
          {travel.travel_images.slice(1).map((img) => (
            <img key={img.id} src={img.image_url} alt={travel.title} />
          ))}
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="travel-recommendations">
          <h2 className="recommendations-title">Preporuke restorana i jela</h2>
          <div className="recommendations-grid">
            {recommendations.map((rec, index) => (
              <div key={rec.id || index} className="recommendation-card">
                {rec.image_url && (
                  <div className="recommendation-image">
                    <img src={rec.image_url} alt={`Preporuka ${index + 1}`} />
                  </div>
                )}
                <div className="recommendation-content">
                  <h3 className="recommendation-number">Preporuka {index + 1}</h3>
                  {rec.description && (
                    <p className="recommendation-description">{rec.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="travel-actions">
        <div className="travel-actions-row">
          <button
            type="button"
            className="travel-share-btn"
            onClick={handleShareTravel}
            aria-label="Podijeli putovanje"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
            </svg>
            <span>Podijeli</span>
          </button>
          <Link to="/putovanja" className="btn-back">
            ← Povratak na putovanja
          </Link>
        </div>
        {shareHint && (
          <p className="share-hint" role="status">
            {shareHint}
          </p>
        )}
      </div>
    </div>
  );
}



